<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { InputGroupSize } from './InputGroup.vue'
import { computed, inject } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { INPUT_GROUP_KEY } from './InputGroup.vue'

const props = defineProps<{
  class?: HTMLAttributes['class']
}>()

const group = inject(INPUT_GROUP_KEY, null)

const iconSizes: Record<InputGroupSize, string> = {
  xs: "[&>svg:not([class*='size-'])]:size-3",
  sm: "[&>svg:not([class*='size-'])]:size-3",
  md: "[&>svg:not([class*='size-'])]:size-4",
  lg: "[&>svg:not([class*='size-'])]:size-5",
  xl: "[&>svg:not([class*='size-'])]:size-6",
}

const iconSize = computed(() => iconSizes[group?.size.value ?? 'md'])

// An addon that holds a button sits flush with the group's border on its outer side.
// The margins undo the group's horizontal padding; the 1px padding keeps the group ring visible.
const flushSizes: Record<InputGroupSize, string> = {
  xs: 'has-[>button]:first:-ms-2 has-[>button]:last:-me-2',
  sm: 'has-[>button]:first:-ms-2 has-[>button]:last:-me-2',
  md: 'has-[>button]:first:-ms-2 has-[>button]:last:-me-2',
  lg: 'has-[>button]:first:-ms-3 has-[>button]:last:-me-3',
  xl: 'has-[>button]:first:-ms-4 has-[>button]:last:-me-4',
}

const flush = computed(() => [
  'has-[>button]:self-stretch has-[>button]:p-px',
  'has-[>button]:first:[&>button]:rounded-s-md has-[>button]:last:[&>button]:rounded-e-md',
  flushSizes[group?.size.value ?? 'md'],
])

// Clicking a text or icon addon lands the caret in the input; a button keeps its own click.
function onClick(event: MouseEvent) {
  if (event.target instanceof Element && event.target.closest('button')) {
    return
  }
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.parentElement?.querySelector('input')?.focus()
  }
}
</script>

<template>
  <div
    data-slot="input-group-addon"
    :class="
      cn('flex shrink-0 select-none items-center gap-1 text-nowrap [&>svg]:shrink-0', iconSize, flush, props.class)
    "
    @click="onClick"
  >
    <slot />
  </div>
</template>
